/**
 * <hero-video-fade-slider>
 * A small, self-contained autoplaying crossfade slider for the hero video
 * section. Slides are stacked (CSS grid, single cell) and cross-fade via
 * opacity; the grid auto-sizes to the tallest slide so full, un-cropped
 * video is always visible instead of being clipped to a fixed height.
 */
class HeroVideoFadeSlider extends HTMLElement {
  #timer = null;
  #index = 0;
  #slides = [];
  #dots = [];

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll(':scope > .hero-fade-slide'));
    this.#dots = Array.from(this.querySelectorAll(':scope [data-hero-fade-dot]'));

    if (this.#slides.length === 0) return;

    this.#show(0, { instant: true });

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.#show(i);
        this.#restartAutoplay();
      });
    });

    this.querySelectorAll('[data-hero-fade-prev]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#show(this.#index - 1);
        this.#restartAutoplay();
      });
    });

    this.querySelectorAll('[data-hero-fade-next]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#show(this.#index + 1);
        this.#restartAutoplay();
      });
    });

    if (this.#slides.length > 1 && this.dataset.autoplay === 'true' && !this.#prefersReducedMotion()) {
      this.addEventListener('pointerenter', this.#pauseAutoplay);
      this.addEventListener('pointerleave', this.#startAutoplay);
      this.addEventListener('focusin', this.#pauseAutoplay);
      this.addEventListener('focusout', this.#startAutoplay);
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    this.#pauseAutoplay();
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #show(index, { instant = false } = {}) {
    const total = this.#slides.length;
    const nextIndex = ((index % total) + total) % total;

    this.#slides.forEach((slide, i) => {
      const isActive = i === nextIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      const video = slide.querySelector('video');
      if (video) {
        if (isActive) {
          video.play?.().catch(() => {});
        } else if (!instant) {
          video.pause?.();
        }
      }
    });

    this.#dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === nextIndex);
      dot.setAttribute('aria-current', i === nextIndex ? 'true' : 'false');
    });

    this.#index = nextIndex;
  }

  #startAutoplay = () => {
    this.#pauseAutoplay();
    const speed = Number(this.dataset.speed) || 6000;
    this.#timer = window.setInterval(() => this.#show(this.#index + 1), speed);
  };

  #pauseAutoplay = () => {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #restartAutoplay() {
    if (this.#timer !== null) this.#startAutoplay();
  }
}

customElements.define('hero-video-fade-slider', HeroVideoFadeSlider);
